import React, { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Row, Col, Divider, Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import StatusBadge from '../../../components/Badges/StatusBadge';
import HeaderLabel from '../../../components/Form/HeaderLabel';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import AlumlyButton from '../../../components/AlumlyButton';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { PathBuilder } from '../../../utils/pathBuilder';
import DeleteNewsModal from '../components/DeleteNewsModal';

const NewsDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [detail, setDetail] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { service: getDetailService } = useServices(ServiceNameEnum.news.detail);

  const getDetail = async (): Promise<void> => {
    const params = { id };
    const { data, error } = await getDetailService.get({ params });
    if (data) {
      setDetail(data);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
      navigate(RouteNameEnum.news.index);
    }
  };

  useEffect(() => { getDetail(); }, []);

  const path: PathT[] = [
    { label: 'News', value: RouteNameEnum.news.index },
    { label: 'News detail' },
  ];

  const getML = (ml: any, key: string): string => ml?.[key] || '-';

  return (
    <div>
      <AlumlyBreadCrumb path={path} />
      <Typography.Title level={4} style={{ fontWeight: 'bold' }}>News Detail</Typography.Title>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          <Typography.Text type="secondary">status</Typography.Text>
          <StatusBadge status={!!detail?.isPublished} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          {checkPermission([PermissionEnum.code.newsDelete]) && (
            <AlumlyButton label="rxvWhite" icon={<DeleteIcon />} width={200} onClick={() => setOpenDeleteModal(true)}>
              Delete
            </AlumlyButton>
          )}
          {checkPermission([PermissionEnum.code.newsEdit]) && (
            <AlumlyButton label="rxv" icon={<EditIcon />} width={200} onClick={() => navigate(PathBuilder.buildPath(RouteNameEnum.news.edit, { params: { id } }))}>
              Edit
            </AlumlyButton>
          )}
        </div>
      </div>

      <DeleteNewsModal newsId={Number(id)} isOpen={openDeleteModal} handleClose={() => setOpenDeleteModal(false)} />

      {/* Cover Image */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Cover Image" />
        <div style={{ background: 'white', padding: 16 }}>
          {detail?.coverFile?.presignUrl ? (
            <img
              src={detail.coverFile.presignUrl}
              alt=""
              style={{
                width: 300, height: 200, borderRadius: 4, objectFit: 'cover',
              }}
            />
          ) : (
            <Typography.Text type="secondary">No image</Typography.Text>
          )}
        </div>
      </div>

      {/* Title */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Title" />
        <div style={{ background: 'white', padding: 16 }}>
          <Row gutter={[32, 32]}>
            <Col xs={24} md={12}>
              <Typography.Text type="secondary">Title (EN)</Typography.Text>
              <Typography.Paragraph>{getML(detail?.titleML, 'en')}</Typography.Paragraph>
            </Col>
            <Col xs={24} md={12}>
              <Typography.Text type="secondary">Title (TH)</Typography.Text>
              <Typography.Paragraph>{getML(detail?.titleML, 'th')}</Typography.Paragraph>
            </Col>
          </Row>
        </div>
      </div>

      {/* Description */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Description" />
        <div style={{ background: 'white', padding: 16 }}>
          <Row gutter={[32, 32]}>
            <Col xs={24} md={12}>
              <Typography.Text type="secondary">Description (EN)</Typography.Text>
              <div style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: getML(detail?.descriptionML, 'en') }} />
            </Col>
            <Col xs={24} md={12}>
              <Typography.Text type="secondary">Description (TH)</Typography.Text>
              <div style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: getML(detail?.descriptionML, 'th') }} />
            </Col>
          </Row>
        </div>
      </div>

      {/* Link URL */}
      {detail?.linkUrl && (
        <div style={{ marginTop: 32 }}>
          <HeaderLabel label="Link URL" />
          <div style={{ background: 'white', padding: 16 }}>
            <Typography.Paragraph>{detail.linkUrl}</Typography.Paragraph>
          </div>
        </div>
      )}

      <Divider style={{ marginTop: 32, marginBottom: 32 }} />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <AlumlyButton label="rxvWhite" width={128} onClick={() => navigate(RouteNameEnum.news.index)}>
          <span>Back</span>
        </AlumlyButton>
      </div>
    </div>
  );
};

export default NewsDetail;
